import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {MilestoneIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {Tooltip} from '@primer/react/next'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import styles from './DemilestonedEvent.module.css'
import type {DemilestonedEventNode} from './issue-viewer-page-query-types'
import type {MilestonedEventFragment} from './MilestonedEvent'
import {RolledupMilestonedEvent} from './RolledupMilestonedEvent'
import {TimelineRow} from './row/TimelineRow'

// RELAY-CLEANUP(DemilestonedEvent): widens nullable fields so still-Relay parent data is assignable.
// Remove when the parent timeline query leaves Relay.
export type DemilestonedEventFragment = RelayCompat<
  Pick<DemilestonedEventNode, 'actor' | 'createdAt' | 'databaseId' | 'milestone' | 'milestoneTitle'>
>

type DemilestonedEventProps = {
  queryRef: DemilestonedEventFragment
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  rollupGroup?: Record<string, Array<MilestonedEventFragment | DemilestonedEventFragment>>
}

export function DemilestonedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  rollupGroup,
}: DemilestonedEventProps): React.ReactElement {
  const {actor, createdAt, milestoneTitle, milestone, databaseId} = queryRef

  const highlighted = String(databaseId) === highlightedEventId

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={MilestoneIcon}
    >
      <TimelineRow.Main>
        {rollupGroup ? (
          <RolledupMilestonedEvent rollupGroup={rollupGroup} />
        ) : (
          <>
            {LABELS.timeline.removedFromMilestone}
            {getWrappedMilestoneLink(milestone?.url, milestoneTitle)}
            {LABELS.timeline.milestone}{' '}
          </>
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

export function getWrappedMilestoneLink(
  url: string | undefined,
  title: string,
  addDelimiter?: boolean,
): React.ReactElement {
  return (
    <>
      {' '}
      {url !== undefined ? (
        <Link href={url} inline className={styles.milestoneLink}>
          {title}
        </Link>
      ) : (
        <Tooltip text={LABELS.timeline.milestoneDeleted}>
          <Link as="button" type="button" aria-disabled="true" inline className={styles.milestoneLink}>
            {title}
          </Link>
        </Tooltip>
      )}
      {addDelimiter && ','}{' '}
    </>
  )
}
